import { TrackVisibilityService } from '@src/services/timeline/tracks/TrackVisibilityService';
import { useStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CircleIcon from '@src/svgs/CircleIcon';
import EyeClosedIcon from '@src/svgs/EyeClosedIcon';
import EyeOpenIcon from '@src/svgs/EyeOpenIcon';
import FrameLockIcon from '@src/svgs/FrameLockIcon';
import { getElementPreviewMeta } from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import {
  getTimelineKindColor,
  resolveTimelinePreview,
  resolveTrackDotColor,
} from '@src/utility/timelinePreview';
import { getTrackStyleFlags } from '@src/utility/trackTypes';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';

import type { ElementState } from '@src/renderer/ElementState';
import type { TrackType } from '@src/utility/trackTypes';

interface TimelineTrackLabelsProps {
  tracks: [number, ElementState[]][];
  activeTracks: number[];
  offsetY: number;
  getTrackInfo: (
    track: number,
    elements: ElementState[],
  ) => {
    type: TrackType;
    isActive: boolean;
    height: number;
  };
}

type TrackInfo = ReturnType<TimelineTrackLabelsProps['getTrackInfo']>;
type TrackSlots = {
  circleSlot: React.ReactNode;
  labelSlot: React.ReactNode;
  lockSlot: React.ReactNode | null;
};

/**
 * Extended track info with derived boolean flags for rendering logic.
 */
interface DerivedTrackInfo extends TrackInfo {
  isOriginalVideo: boolean;
  isLogo: boolean;
  isShape: boolean;
  isComposition: boolean;
  isKaraoke: boolean;
  isText: boolean;
  isAudio: boolean;
  isVideo: boolean;
  isImage: boolean;
}

const logger = LogManager.getLogger('component:TimelineTrackLabels');

function getLockFrameMessage(isLocked: boolean): string {
  return isLocked
    ? 'Unlock framing across elements on the track'
    : 'Lock framing across elements on the track';
}

export const TimelineTrackLabels: React.FC<TimelineTrackLabelsProps> = observer(
  ({ tracks, offsetY, getTrackInfo }) => {
    const videoCreator = useVideoCreatorStore();
    const { editorStore } = useStore();
    const userIdentity = useUserIdentity();
    const {
      contentStudioTimelineEnableLockFrame,
      creatorStudioTimelineEnableLockFraming,
    } = useFlagsCombination(userIdentity.currentRole);
    const trackVisibilityService = useMemo(
      () => new TrackVisibilityService({ videoCreator, editorStore }),
      [videoCreator, editorStore],
    );

    const isTrackHidden = (elements: ElementState[]) =>
      elements.every(element => element.source?.visible === false);

    const renderTrackContent = (
      track: number,
      elements: ElementState[],
      trackInfo: DerivedTrackInfo,
    ): TrackSlots => {
      const {
        isOriginalVideo,
        isLogo,
        isShape,
        isComposition,
        isKaraoke,
        isAudio,
        isVideo,
        isImage,
      } = trackInfo;

      const buildLockButton = () => {
        if (
          !contentStudioTimelineEnableLockFrame &&
          !creatorStudioTimelineEnableLockFraming
        ) {
          return null;
        }
        const lockTooltipId = `framing-lock-tooltip-${track}`;
        const isLocked = videoCreator.frameLockedTracks.includes(track);
        return (
          <ControlButton
            type="button"
            isControlEnabled={isLocked}
            onClick={() =>
              videoCreator.trackManager.toggleTrackFrameLock(track)
            }
            data-tooltip-id={lockTooltipId}
            data-tooltip-content={getLockFrameMessage(isLocked)}
          >
            <FrameLockIcon fillColor="currentColor" />
            <Tooltip
              id={lockTooltipId}
              style={COMPACT_TOOLTIP_STYLE}
              place="right"
              positionStrategy="fixed"
            />
          </ControlButton>
        );
      };

      const baseSlots: TrackSlots = {
        circleSlot: <CircleIcon fillColor={getTimelineKindColor('text')} />,
        labelSlot: <TrackLabelText>Text</TrackLabelText>,
        lockSlot: null,
      };

      if (isOriginalVideo) {
        // PLAT-11228 audio-first: the main track plays the editor audio
        // while the video renditions are still processing.
        const audioFirstPending =
          !!videoCreator.story &&
          isCompressedRenditionPending(videoCreator.story);
        return {
          circleSlot: (
            <CircleIcon
              fillColor={getTimelineKindColor(
                audioFirstPending ? 'audio' : 'video',
              )}
            />
          ),
          labelSlot: (
            <TrackLabelText>
              {audioFirstPending ? 'Audio Only · Video Processing' : 'Video'}
            </TrackLabelText>
          ),
          lockSlot: buildLockButton(),
        };
      }

      if (isLogo) {
        return {
          ...baseSlots,
          circleSlot: <CircleIcon fillColor={getTimelineKindColor('logo')} />,
          labelSlot: <TrackLabelText>Logo</TrackLabelText>,
        };
      }

      if (isImage) {
        return {
          ...baseSlots,
          circleSlot: <CircleIcon fillColor={getTimelineKindColor('photo')} />,
          labelSlot: <TrackLabelText>Photo</TrackLabelText>,
          lockSlot: buildLockButton(),
        };
      }

      if (isVideo) {
        return {
          ...baseSlots,
          circleSlot: <CircleIcon fillColor={getTimelineKindColor('video')} />,
          labelSlot: <TrackLabelText>B-Roll</TrackLabelText>,
          lockSlot: buildLockButton(),
        };
      }

      if (isAudio) {
        return {
          ...baseSlots,
          circleSlot: <CircleIcon fillColor={getTimelineKindColor('audio')} />,
          labelSlot: <TrackLabelText>Music</TrackLabelText>,
        };
      }

      if (isShape) {
        return {
          ...baseSlots,
          circleSlot: <CircleIcon fillColor={getTimelineKindColor('shape')} />,
          labelSlot: <TrackLabelText>Shape</TrackLabelText>,
        };
      }

      if (isComposition) {
        // PLAT-11816 — the dot reads the same token as the blocks on the track.
        const dotColor = resolveTrackDotColor(
          'composition',
          elements.map(el =>
            resolveTimelinePreview({
              trackType: 'composition',
              meta: getElementPreviewMeta(
                videoCreator.currentVideo,
                el.source.id,
              ),
            }),
          ),
        );
        return {
          ...baseSlots,
          circleSlot: <CircleIcon fillColor={dotColor} />,
          labelSlot: <TrackLabelText>Graphics</TrackLabelText>,
        };
      }

      if (isKaraoke) {
        return {
          ...baseSlots,
          labelSlot: <TrackLabelText>Captions</TrackLabelText>,
        };
      }

      return baseSlots;
    };

    const renderVisibilityToggle = (
      track: number,
      isOriginalVideo: boolean,
      isAudioTrack: boolean,
      hidden: boolean,
    ) => {
      if (isOriginalVideo || isAudioTrack) {
        return null;
      }

      const tooltipId = `track-visibility-tooltip-${track}`;
      const tooltipContent = hidden ? 'Show track' : 'Hide track';

      return (
        <ControlButton
          type="button"
          aria-label={tooltipContent}
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltipContent}
          onClick={event => {
            event.stopPropagation();
            // TrackVisibilityService expects the desired "visible" value.
            // When the track is currently hidden we want to show it (visible=true),
            // otherwise we hide it (visible=false);
            const targetVisible = hidden;
            const result = trackVisibilityService.toggleTrackVisibility(
              track,
              targetVisible,
            );
            if (!result.ok) {
              logger.warn('Track visibility toggle failed', {
                track,
                targetVisible,
                error: result.error,
              });
            }
          }}
          isControlEnabled={hidden}
        >
          {hidden ? (
            <EyeClosedIcon strokeColor="currentColor" />
          ) : (
            <EyeOpenIcon strokeColor="currentColor" />
          )}
          <Tooltip
            id={tooltipId}
            style={COMPACT_TOOLTIP_STYLE}
            place="top"
            positionStrategy="fixed"
          />
        </ControlButton>
      );
    };

    return (
      <LeftTrackWrapper>
        <LeftTrackWrapperInner style={{ top: -1 * offsetY }}>
          {tracks.map(([track, elements]) => {
            const trackInfo = getTrackInfo(track, elements);
            const { isActive } = trackInfo;

            // Derive styling booleans from track type
            const styleFlags = getTrackStyleFlags(trackInfo.type);
            const derivedInfo: DerivedTrackInfo = {
              ...trackInfo,
              ...styleFlags,
            };

            const hidden = isTrackHidden(elements);
            const slots = renderTrackContent(track, elements, derivedInfo);

            return (
              <LeftTrack
                key={`track-${track}`}
                linkedVideo={styleFlags.isOriginalVideo}
                isText={styleFlags.isText || styleFlags.isKaraoke}
                isShape={styleFlags.isShape}
                isComposition={styleFlags.isComposition}
                isImage={styleFlags.isImage}
                isActive={isActive}
                onClick={() => videoCreator.timelineStore.selectTrack(track)}
                isLogo={styleFlags.isLogo}
              >
                <CircleCell>{slots.circleSlot}</CircleCell>
                <TextCell>{slots.labelSlot}</TextCell>
                <ControlCell>
                  {slots.lockSlot ?? <ControlPlaceholder aria-hidden="true" />}
                </ControlCell>
                <ControlCell>
                  {renderVisibilityToggle(
                    track,
                    styleFlags.isOriginalVideo,
                    styleFlags.isAudio,
                    hidden,
                  ) ?? <ControlPlaceholder aria-hidden="true" />}
                </ControlCell>
              </LeftTrack>
            );
          })}
        </LeftTrackWrapperInner>
      </LeftTrackWrapper>
    );
  },
);

const LeftTrackWrapper = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: calc(100% - 35px);
  padding-bottom: 100px;
  box-sizing: border-box;
  overflow: hidden;
`;

const LeftTrackWrapperInner = styled.div`
  position: absolute;
  width: 100%;
`;

const LeftTrack = styled.div<{
  linkedVideo: boolean;
  isText: boolean;
  isActive: boolean;
  isLogo?: boolean;
  isShape: boolean;
  isComposition: boolean;
  isImage?: boolean;
}>`
  position: relative;
  height: ${props => {
    if (props.linkedVideo) return '129px';
    if (
      props.isText ||
      props.isLogo ||
      props.isShape ||
      props.isComposition ||
      props.isImage
    )
      return '38px';
    return '78px';
  }};
  ${props =>
    props.isActive
      ? 'background-color: color-mix(in srgb, var(--border) 50%, transparent);'
      : ''}
  display: grid;
  grid-template-columns: 8px 62px 16px 16px;
  column-gap: 8px;
  padding-left: 32px;
  padding-right: 0;
  align-items: center;
  cursor: pointer;
`;

const CircleCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const TextCell = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const ControlCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
`;

const ControlPlaceholder = styled.div`
  width: 100%;
  height: 28px;
`;

const TrackLabelText = styled.span`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ControlButton = styled.button<{ isControlEnabled: boolean }>`
  outline: 0;
  width: 16px;
  height: 16px;
  background-color: ${props =>
    props.isControlEnabled ? 'var(--accent)' : 'transparent'};
  color: ${props =>
    props.isControlEnabled ? 'var(--background)' : 'var(--accent)'};
  border: 2px solid
    ${props => (props.isControlEnabled ? 'var(--accent)' : 'transparent')};
  border-radius: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
  ${props =>
    !props.isControlEnabled
      ? `
      &:hover {
        background-color: var(--text-secondary);
        color: #000000;
        border: 2px solid var(--text-secondary);
      }
    `
      : ''}
`;
